import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../services/api";
import {
  Bell, Star, StarOff, Trash2, Archive, CheckCircle, Circle, Settings, X
} from "lucide-react";

// --- Move this outside the component! ---
function normalizeNotif(n) {
  return {
    ...n,
    id: n.notification_id,
    read: !!n.is_read,
    archived: n.archived ? !!n.archived : false,
    star: n.star ? !!n.star : false,
  };
}

export default function NotificationBell({ theme = {} }) {
  // Theme/color vars
  const notifAccent = theme.color || "#2c90fc";
 // const notifIconStyle = theme.iconStyle || "filled";
  const unreadColor = theme.unreadColor || notifAccent;
  const readColor = theme.readColor || "#c5d2eb";
  const accentColor = theme.accentColor || notifAccent;

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef();

  // --- Now fetchNotifications is stable, usesCallback, and lint-proof ---
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/users/me/notifications");
      const notifs = (res.data || []).map(normalizeNotif);
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read && !n.archived).length);
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    }
    setLoading(false);
  }, []);

  // --- All event handlers below (as before, no need for useCallback unless you want to micro-optimize) ---
  async function markAsRead(id) {
    await api.post(`/users/me/notifications/${id}/read`);
    setNotifications(notifications =>
      notifications.map(n => n.id === id ? { ...n, read: true } : n)
    );
    setUnreadCount(c => Math.max(0, c - 1));
  }

  async function markAllAsRead() {
    await api.post("/users/me/notifications/read-all");
    setNotifications(notifications =>
      notifications.map(n => n.read || n.archived ? n : { ...n, read: true })
    );
    setUnreadCount(0);
  }

  async function archiveNotif(id) {
    await api.post(`/users/me/notifications/${id}/archive`);
    setNotifications(notifications =>
      notifications.map(n => n.id === id ? { ...n, archived: true } : n)
    );
  }

  async function toggleStar(id, value) {
    await api.post(`/users/me/notifications/${id}/star`, { star: value });
    setNotifications(notifications =>
      notifications.map(n => n.id === id ? { ...n, star: value } : n)
    );
  }

  async function deleteNotif(id) {
    if (!window.confirm("Delete this notification forever?")) return;
    await api.delete(`/users/me/notifications/${id}`);
    setNotifications(notifications => notifications.filter(n => n.id !== id));
    setUnreadCount(notifications => notifications.filter(n => !n.read && n.id !== id).length);
  }

  async function deleteAll() {
    if (!window.confirm("Delete ALL notifications forever?")) return;
    await api.delete("/users/me/notifications/delete-all");
    setNotifications([]);
    setUnreadCount(0);
  }

  async function clearAll() {
    if (!window.confirm("Clear all notifications? (Archived will remain)")) return;
    await api.post("/users/me/notifications/clear-all");
    setNotifications(notifications => notifications.filter(n => n.archived));
    setUnreadCount(0);
  }

  // Show only active (not archived) notifications in dropdown
  const inbox = notifications.filter(n => !n.archived);

  // -- Effects --
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    if (open && dropdownRef.current) dropdownRef.current.focus();
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function handleKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [open]);

  // -- Render --
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        aria-label="Notifications"
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none",
          border: "none",
          position: "relative",
          cursor: "pointer",
          outline: open ? `2px solid ${accentColor}` : "none",
        }}
      >
        <Bell color={accentColor} size={26} />
        {unreadCount > 0 && (
          <span style={{
            position: "absolute",
            top: -6,
            right: -3,
            background: unreadColor,
            color: "#fff",
            fontWeight: 700,
            borderRadius: "50%",
            fontSize: 13,
            minWidth: 18,
            height: 18,
            lineHeight: "18px",
            textAlign: "center",
            boxShadow: "0 2px 6px #2234",
            zIndex: 'var(--z-dropdown)',
            padding: "0 3px"
          }}>
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={dropdownRef}
          className="notif-dropdown"
          style={{
            position: "absolute",
            top: 36,
            right: 0,
            width: 355,
            maxWidth: "95vw",
            maxHeight: 480,
            background: "#fff",
            borderRadius: 13,
            boxShadow: "0 8px 32px #2234, 0 1.5px 8px #0001",
            padding: "13px 2px 10px 0",
            zIndex: 'var(--z-dropdown)',
            overflow: "hidden",
            outline: "none",
            transition: "box-shadow 0.14s"
          }}
          tabIndex={-1}
        >
          <div style={{ padding: "0 18px 2px 18px", display: "flex", alignItems: "center", gap: 11 }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>Notifications</span>
            <button onClick={markAllAsRead} style={{ background: "none", color: unreadColor, fontSize: 13, marginLeft: "auto" }}>
              <CheckCircle size={18} /> Mark all as read
            </button>
            <button onClick={clearAll} title="Clear inbox" style={{ background: "none", color: "#888", fontSize: 13 }}>
              <X size={17} /> Clear all
            </button>
            <button onClick={deleteAll} title="Delete all" style={{ background: "none", color: "#a21", fontSize: 13 }}>
              <Trash2 size={17} /> Delete all
            </button>
            <button onClick={() => alert("Notification Preferences coming soon!")} title="Preferences" style={{ background: "none", color: accentColor, fontSize: 13 }}>
              <Settings size={17} />
            </button>
          </div>
          <hr style={{ border: 0, borderTop: "1px solid #eee", margin: "8px 0" }} />
          <div style={{ maxHeight: 350, overflowY: "auto", padding: "0 13px" }}>
            {loading && <div style={{ textAlign: "center", padding: 28 }}>Loading...</div>}
            {!loading && inbox.length === 0 && (
              <div style={{ textAlign: "center", color: "#777", padding: "24px 2px" }}>
                No notifications!
              </div>
            )}
            {!loading && inbox.map(n => (
              <div
                key={n.id}
                style={{
                  background: n.read ? "#f7fafb" : "#e7f0fa",
                  borderLeft: `5px solid ${n.read ? readColor : unreadColor}`,
                  borderRadius: 9,
                  marginBottom: 8,
                  padding: "10px 10px 7px 13px",
                  display: "flex",
                  alignItems: "flex-start",
                  position: "relative",
                  boxShadow: n.star ? `0 0 0 2px ${accentColor}22` : "none"
                }}
              >
                {/* Icon based on type */}
                <div style={{ marginRight: 11, marginTop: 1 }}>
                  {n.type === "system" && <Circle color={unreadColor} size={20} />}
                  {n.type === "promo" && <Star color="#fcae2c" size={20} />}
                  {n.type === "alert" && <Bell color="#a21" size={20} />}
                  {!["system", "promo", "alert"].includes(n.type) && <Circle color={readColor} size={19} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: n.read ? 400 : 600,
                    color: n.read ? "#567" : "#000"
                  }}>
                    {n.message}
                  </div>
                  <div style={{ fontSize: 12, color: "#899", marginTop: 1 }}>
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 1, marginLeft: 5 }}>
                  <button
                    title={n.star ? "Unpin" : "Pin"}
                    style={{ background: "none", color: n.star ? accentColor : "#bbb", border: "none", cursor: "pointer", padding: 0 }}
                    onClick={() => toggleStar(n.id, !n.star)}
                  >
                    {n.star ? <Star size={18} /> : <StarOff size={18} />}
                  </button>
                  {!n.read && (
                    <button
                      title="Mark as read"
                      style={{ background: "none", color: unreadColor, border: "none", cursor: "pointer", padding: 0 }}
                      onClick={() => markAsRead(n.id)}
                    >
                      <CheckCircle size={18} />
                    </button>
                  )}
                  <button
                    title="Archive"
                    style={{ background: "none", color: "#888", border: "none", cursor: "pointer", padding: 0 }}
                    onClick={() => archiveNotif(n.id)}
                  >
                    <Archive size={18} />
                  </button>
                  <button
                    title="Delete"
                    style={{ background: "none", color: "#c00", border: "none", cursor: "pointer", padding: 0 }}
                    onClick={() => deleteNotif(n.id)}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding: "8px 13px 0 18px", textAlign: "right" }}>
            <button
              onClick={() => alert("Coming soon: Full notification center!")}
              style={{
                background: "none",
                color: accentColor,
                border: "none",
                fontSize: 14,
                cursor: "pointer",
                textDecoration: "underline"
              }}
            >
              View all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
